import { dxfFloat, areFloatsEqual } from "../NodeDXF/dxfFloat";
import { Point } from "../DrawingObj";
import { Element } from "./Element";
function mirrorArcOnXAxis(arc) {
    const mirroredCenterX = arc.centerX * -1;
    const mirroredStartAngle = arc.endAngle;
    const mirroredEndAngle = arc.startAngle;
    let newStartAngle, newEndAngle;
    if (arc.startAngle <= 180 && arc.endAngle <= 180) {
        newStartAngle = 180 - mirroredStartAngle;
        newEndAngle = 180 - mirroredEndAngle;
    }
    else {
        newStartAngle = 540 - mirroredStartAngle;
        newEndAngle = 540 - mirroredEndAngle;
    }
    return {
        centerX: mirroredCenterX,
        centerY: arc.centerY,
        radius: arc.radius,
        startAngle: newStartAngle,
        endAngle: newEndAngle,
    };
}
function calcBindingRect(centerX, centerY, radius, startAngle, endAngle) {
    const startPoint = {
        x: centerX + radius * Math.cos(startAngle),
        y: centerY + radius * Math.sin(startAngle),
    };
    const endPoint = {
        x: centerX + radius * Math.cos(endAngle),
        y: centerY + radius * Math.sin(endAngle),
    };
    const { x: startX, y: startY } = startPoint;
    const { x: endX, y: endY } = endPoint;
    let OverPi = endAngle - startAngle < Math.PI;
    let HozCheckA = endY < centerY;
    let HozCheckB = startY < centerY;
    let VertCheckA = endX > centerX && startX < centerX;
    let VertCheckB = endX < centerX && startX > centerX;
    const rect = {
        left: (!OverPi && !HozCheckB) || (OverPi && HozCheckA)
            ? centerX - radius
            : startX,
        right: (!OverPi && !HozCheckA) || (OverPi && HozCheckB)
            ? centerX + radius
            : startX,
        top: (!OverPi && !VertCheckB) || (OverPi && VertCheckA)
            ? centerY - radius
            : endY,
        bottom: (!OverPi && !VertCheckA) || (OverPi && VertCheckB)
            ? centerY + radius
            : endY,
        height: 0,
        width: 0,
    };
    if (endX < rect.left)
        rect.left = endX;
    if (endX > rect.right)
        rect.right = endX;
    if (startY < rect.top)
        rect.top = startY;
    if (startY > rect.bottom)
        rect.bottom = startY;
    rect.height = Math.abs(rect.bottom - rect.top);
    rect.width = Math.abs(rect.right - rect.left);
    return rect;
}
export class Arc extends Element {
    constructor(element, retry = false) {
        super(element);
        this.StartAngle = undefined;
        this.EndAngle = undefined;
        this.width = undefined;
        this.height = undefined;
        this.isCircle = false;
        this.ArcPoints = [];
        this.rect = null;
        if (this.element.AcDbCircle) {
            this.points = [new Point(element.AcDbCircle.VARS, 10)];
            this.Radius = dxfFloat(element.AcDbCircle.Radius);
        }
        if (element.AcDbArc) {
            if (element.AcDbArc.StartAngle || element.AcDbArc.StartAngle === 0)
                this.StartAngle = parseFloat(element.AcDbArc.StartAngle);
            if (element.AcDbArc.EndAngle || element.AcDbArc.EndAngle === 0)
                this.EndAngle = parseFloat(element.AcDbArc.EndAngle);
        }
        if (this.points.length <= 0) {
            this.Radius = dxfFloat(element.Radius);
            this.points = [new Point(element.VARS, 10)];
        }
        if (this.StartAngle === undefined && element.VARS) {
            this.StartAngle = element.VARS.Angle0
                ? parseFloat(element.VARS.Angle0)
                : element.StartAngle;
        }
        if (this.EndAngle === undefined && element.VARS) {
            this.EndAngle = element.VARS.Angle1
                ? parseFloat(element.VARS.Angle1)
                : element.EndAngle;
        }
        if (this.EndAngle === undefined) {
            // console.error("No EndAngle", this);
            this.EndAngle = Math.PI * 2;
        }
        if (this.StartAngle === undefined) {
            // console.error("No StartAngle", this);
            this.StartAngle = 0;
        }
        this.CircleCheck();
        if (!this.isCircle) {
            if (this.StartAngle !== undefined) {
                let x_new = this.points[0].x + this.Radius * Math.cos(this.StartAngle);
                let y_new = this.points[0].y + this.Radius * Math.sin(this.StartAngle);
                this.ArcPoints.push(Point.fromXYZ(x_new, y_new, 0));
            }
            if (this.EndAngle !== undefined) {
                let x_new2 = this.points[0].x + this.Radius * Math.cos(this.EndAngle);
                let y_new2 = this.points[0].y + this.Radius * Math.sin(this.EndAngle);
                this.ArcPoints.push(Point.fromXYZ(x_new2, y_new2, 0));
            }
        }
        this.ExtrusionCheck();
        this.CalcSize();
    }
    CalcSize() {
        let mirroredStartAngle = this.StartAngle;
        let mirroredEndAngle = this.EndAngle;
        if (this.Extrusion) {
            mirroredStartAngle = Math.PI - this.StartAngle;
            mirroredEndAngle = Math.PI - this.EndAngle;
        }
        let binding = calcBindingRect(this.points[0].x, this.points[0].y, this.Radius, mirroredStartAngle, mirroredEndAngle);
        this.height = binding.height;
        this.width = binding.width;
        this.rect = binding;
        if (this.Radius < 0.02)
            this.Visible = false;
    }
    CircleCheck() {
        this.isCircle = this.StartAngle === this.EndAngle;
        if (!this.isCircle &&
            ((areFloatsEqual(this.StartAngle, Math.PI * 2) &&
                areFloatsEqual(this.EndAngle, 0)) ||
                (areFloatsEqual(this.StartAngle, 0) &&
                    areFloatsEqual(this.EndAngle, Math.PI * 2))))
            this.isCircle = true;
    }
    maxX(i) {
        if (!this.Visible)
            return i;
        if (this.isCircle)
            return Math.max(i, ...this.points.reduce((acc, { x }) => [...acc, x + this.Radius], []));
        return Math.max(i, this.rect.right, this.rect.left);
    }
    minX(i) {
        if (!this.Visible)
            return i;
        if (this.isCircle)
            return Math.min(i, ...this.points.reduce((acc, { x }) => [...acc, x - this.Radius], []));
        return Math.min(i, this.rect.left, this.rect.right);
    }
    maxY(i) {
        if (!this.Visible)
            return i;
        if (this.isCircle)
            return Math.max(i, ...this.points.reduce((acc, { y }) => [...acc, y + this.Radius], []));
        return Math.max(i, this.rect.bottom, this.rect.top);
    }
    minY(i) {
        if (!this.Visible)
            return i;
        if (this.isCircle)
            return Math.min(i, ...this.points.reduce((acc, { y }) => [...acc, y - this.Radius], []));
        return Math.min(i, this.rect.top, this.rect.bottom);
    }
    length() {
        const circumference = 2 * Math.PI * this.Radius;
        if (this.isCircle)
            return circumference;
        const angularSpan = this.EndAngle - this.StartAngle;
        return Math.abs(circumference * (angularSpan / (2 * Math.PI)));
    }
}
